import React, { useEffect, useState } from 'react';
import {connect} from 'react-redux';
import { Col, Row } from 'reactstrap';
import { useHistory } from 'react-router-dom';
import Pagination from '../../components/pagination';
import { getUserDeviceActivities } from '../../store/actions';
import DevicePerformanceBlock from '../../components/DevicePerformanceBlock';
import AiResponse from '../../components/aiResponse/AiResponse';
import { buildPaginationData } from '../../utility/utils';
import UserDeviceTable from "../../components/tables/UserDeviceTable";
import { ActionModalBody } from '../../components/tables/GoogleDevicesBaseTable/GoogleDevicesBaseTableHelper';
import { updateGoogleDeviceStatus } from "../../pages/GoogleDevices/googleDevicesBaseHelper";
import ActionModal from "../../components/ActionModal";
import ProgressCard from './DeviceCards/ProgressCard';
import TooltipCard from "./DeviceCards/TooltipCard";
import ActionCard from './DeviceCards/ActionCard';
import InfoCard from './DeviceCards/InfoCard';

const DevicePerformance = ({
  loading,
  device,
  deviceActivities,
  getUserDeviceActivities,
  deviceLoading,
  testDetailsData,
  isAiFeaturesEnabled,
  userId,
}) => {
  const [ sortBy, setSortBy ] = useState(null);
  const [ paginationData, setPaginationData ] = useState(null);

  const [action, setAction] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [modalHasErrors, setModalHasError] = useState(false);

  const [isCustomReboot, setIsCustomReboot] = useState(false);
  const [rebootDelayMinutes, setRebootDelay] = useState(null);

  const toSentenceCase = (value) => value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();

  const onModalOpen = (action) => {
		setAction(action);

		setIsConfirmModalOpen(true);
	};

  const onRejectButtonClick = () => {
    setModalHasError(false);
    setIsCustomReboot(false);
    setRebootDelay(null);
    setIsConfirmModalOpen(false);
  }

  const getParams = () => {
    if (action === 'reboot') {
      if (isCustomReboot && rebootDelayMinutes) {
        return { delaySeconds: rebootDelayMinutes * 60 };
      }

      return { delaySeconds: 60 };
    }

    return null;
  }

  const onConfirmButtonClick = () => {
    updateGoogleDeviceStatus(device?.device_id, action, getParams(), setIsLoading);
    setIsCustomReboot(false);
    setRebootDelay(null);
    setIsConfirmModalOpen(false);
  }

  const history = useHistory();

  const fetchData = (page) => {

    let queryParams = { id: userId };

    if (page) {
      queryParams.page = page;
    }

    if (sortBy) {
      queryParams.sort_by = sortBy;
    }

    if (testDetailsData?.guid) {
      const myFilter = {search_by: 'guid', keyword: testDetailsData?.guid};
      queryParams = {...queryParams, ...myFilter};
    }
    getUserDeviceActivities(history, queryParams);
  };

  const systemName = (name) => {
    const lowerName = name?.toLowerCase();

    switch (lowerName) {
      case 'win':
        return 'Windows';

      case ('mac'):
        return 'MacOS';

      case ('cros'):
        return 'Chrome OS';

      case ('lin'):
        return 'Linux';

      default:
        return name;
    }
  };

  const isValidJSON = (text) => {
    try {
      JSON.parse(text);
      return true;
    } catch {
      return false;
    }
  };

  const toGiga = (byte) => {
    return (byte / (1024 * 1024 * 1024)).toFixed(2);
  };

  const toGB = (byte, isRam = false) => {
    const k = isRam ? 1000 : 1024;

    if (!byte) {
      return 0;
    }

    return Number((byte / (k * k * k)).toFixed(2));
  }

  const diffGB = (total, used) => {
    return Number((total - used).toFixed(2));
  }

  const usedPercentage = (used, total) => {
    return Number(((used * 100) / total).toFixed(2))
  }

  const hddInfoResult = (str) => {
    const isJson = isValidJSON(str);
    if (isJson) {
      const hddObject = JSON.parse(str);
      if (hddObject?.length === 0) {
        return 'hide';
      }
      if (typeof hddObject !== 'object') {
        return 'hide';
      }
      return hddObject?.map((item, i) => (
        <div className="device-info-item" key={i}>
          <div className="device-info-name">{item?.name}</div>
          <div className="device-info-value">{`${toGiga(item?.capacity)} GB` || 'No data'}</div>
        </div>
      ));
    }
    return 'hide';
  };

  const batteryStatus = (value) => {
    switch (value) {
      case 0:
        return <span className="text-poor">Poor</span>;
      case 1:
        return <span className="text-good">Good</span>;
      default:
        return <span className="text-custom">Plugged In</span>;
    }
  };

  const cpuStatus = (value) => {
    if (value >= 0 && value <= 21) {
      return <span className="text-good">Average</span>;
    }
    if (value > 21 && value <= 31) {
      return <span className="text-poor">High</span>;
    }
    if (value > 31) {
      return <span className="text-poor">High</span>;
    }
    return <span className="text-custom">Not Available</span>;
  };

  useEffect(() => {
    if (deviceActivities?.device_activities?.length > 0) {
      setPaginationData(buildPaginationData(deviceActivities));
    }
  }, [deviceActivities]);

  useEffect(() => {
    if (userId) {
      fetchData();
    }
  }, [userId]);

  const onTableSortChange = (sortBy) => {
    if (sortBy) {
      setSortBy(sortBy);
      fetchData();
    }
  };

  const navigateToPage = (page) => {
    fetchData(page);
  };

  const aiPostData = {
    user_id: deviceActivities?.device_activities?.[0].user_id
  }

  const isUserid = deviceActivities?.device_activities?.[0].user_id;

  const displayDeviceActions = device?.serial_number;

  const googleDeviceIsActive = device?.device_status === 'ACTIVE';
	const googleDeviceIsDisabled = device?.device_status === 'DISABLED';
	const googleDeviceIsOperational = googleDeviceIsActive || googleDeviceIsDisabled;

  const displayDeviceStats = device?.disk_storage_total && device?.ram_storage_total;

  const totalDiskSpace = toGB(device?.disk_storage_total);
  const usedDiskSpace = toGB(device?.disk_storage_used);
  const freeDiskSpace = diffGB(totalDiskSpace, usedDiskSpace);
  const usedDiskPercentage = usedPercentage(usedDiskSpace, totalDiskSpace);

  const totalRam = toGB(device?.ram_storage_total, true);
  const usedRam = toGB(device?.ram_storage_used, true);
  const freeRam = diffGB(totalRam, usedRam);
  const usedRamPercentage = usedPercentage(usedRam, totalRam);

  const usedCpu = device?.cpu_usage;

  const displayGoogleStats = !!device?.os_version;

  return (
    <>
      {(isUserid && isAiFeaturesEnabled) && (
        <div className="mb-5">
          <AiResponse
            title="device experience summary"
            type="userDeviceTab"
            data={aiPostData}
          />
        </div>
      )}

      {displayDeviceActions && (<DevicePerformanceBlock
        title="Device Actions"
        subtitle="Commands that can be performed remotely on a Google device."
      >
        <Row>
          { googleDeviceIsActive && (<Col xs="12" xl="3" className="mb-4">
            <ActionCard
              cardTitle="Disable Device"
              tooltipId="add"
              tooltipTitle="What does this do?"
              tooltipContent="Disabling a Google device from APIs stops it from communicating with external apps or services. This means the device won’t send or receive data through APIs, effectively removing it from remote management or monitoring."
              buttonText="Disable"
              buttonIcon="mdi-power-off"
              onButtonClick={() => onModalOpen('disable')}
              deviceLoading={deviceLoading || isLoading}
            />
          </Col>)}
          { googleDeviceIsDisabled && (<Col xs="12" xl="3" className="mb-4">
            <ActionCard
              cardTitle="Enable Device"
              tooltipId="aed"
              tooltipTitle="What does this do?"
              tooltipContent="Enabling a Google device through APIs re-establishes its communication with external apps and services. This allows the device to send and receive data through APIs, restoring its connection for remote management, monitoring, and interaction with other systems."
              buttonText="Enable"
              buttonIcon="mdi-power"
              onButtonClick={() => onModalOpen('enable')}
              deviceLoading={deviceLoading || isLoading}
            />
          </Col>)}
          { googleDeviceIsOperational && (<Col xs="12" xl="3" className="mb-4">
            <ActionCard
              cardTitle="Reboot Device"
              tooltipId="ard"
              tooltipTitle="What does this do?"
              tooltipContent="Rebooting a Google device through APIs initiates a system restart, effectively refreshing the device’s software and hardware. This action can resolve temporary issues, apply updates, and restore optimal performance by restarting processes and services on the device."
              buttonText="Reboot"
              buttonIcon="mdi-refresh"
              onButtonClick={() => onModalOpen('reboot')}
              deviceLoading={deviceLoading || isLoading}
            />
          </Col>)}
          { googleDeviceIsOperational && (<Col xs="12" xl="3" className="mb-4">
            <ActionCard
              cardTitle="Wipe Users"
              tooltipId="awu"
              tooltipTitle="What does this do?"
              tooltipContent="Wiping users from a Google device through APIs removes all user data and settings, restoring the device to its factory state. This action deletes personal files, apps, and account information, ensuring that the device is completely cleared of user data before being reassigned or decommissioned."
              buttonText="Wipe"
              buttonIcon="mdi-account-off"
              onButtonClick={() => onModalOpen('wipe')}
              deviceLoading={deviceLoading || isLoading}
            />
          </Col>)}
          { googleDeviceIsOperational && (<Col xs="12" xl="3" className="mb-4">
            <ActionCard
              cardTitle="Powerwash Device"
              tooltipId="apd"
              tooltipTitle="What does this do?"
              tooltipContent="Powerwashing a Google device through APIs restores it to its original factory settings. This action erases all data, including user accounts, settings, and installed apps, effectively resetting the device. It is commonly used when preparing a device for a new user or for troubleshooting issues."
              buttonText="Powerwash"
              buttonIcon="mdi-eraser"
              onButtonClick={() => onModalOpen('powerwash')}
              deviceLoading={deviceLoading || isLoading}
            />
          </Col>)}
        </Row>
      </DevicePerformanceBlock>)}

      <DevicePerformanceBlock
        title="Device Health"
        subtitle="An overview of the device health."
      >
        <Row>
          <Col xs="12" xl="3" className="mb-4">
            <TooltipCard
              cardTitle="Battery Health"
              tooltipId="bh"
              tooltipTitle="What does this mean?"
              tooltipContent="This is a measurement of how long this device can last on a full charge. Battery health is considered good if at a full charge it can last for over 90 minutes."
              cardText={batteryStatus(device?.avg_battery_health)}
              deviceLoading={deviceLoading}
            />
          </Col>
          <Col xs="12" xl="3" className="mb-4">
            <TooltipCard
              cardTitle="CPU Utilization"
              tooltipId="cu"
              tooltipTitle="What does this mean?"
              tooltipContent="This is a measurement of how well this device’s processor can keep up with the workflow. Total CPU that chronically reaches 90% utilization is considered “High” and may indicate insufficient performance, an application issue, or a driver issue."
              cardText={cpuStatus(device?.avg_cpu_utilization)}
              deviceLoading={deviceLoading}
            />
          </Col>
          {device?.avg_cpu_temperature && (
            <Col xs="12" xl="3" className="mb-4">
              <TooltipCard
                cardTitle="Average CPU Temperature"
                tooltipId="act"
                tooltipTitle="What does this mean?"
                tooltipContent="Overheating CPUs can cause poor performance and hardware damage.  CPU temperature exceeding 65C is considered high and the hardware should be inspected."
                cardText={<span className="text-good">{`${device?.avg_cpu_temperature} `}&#x2103;</span>}
                deviceLoading={deviceLoading}
              />
            </Col>
          )}
        </Row>
      </DevicePerformanceBlock>

      { displayDeviceStats && (<DevicePerformanceBlock
        title="Device Stats"
        subtitle="A snapshot of the device’s performance."
      >
        <Row>
          <Col xs="12" xl="3" className="mb-4">
            <ProgressCard
              cardTitle="Disk Space"
              tooltipId="ds"
              tooltipTitle="What does this mean?"
              tooltipContent="This represents the amount of storage currently used on the device. Disk health is considered good if there is at least 5 GB of free space available for system operations and updates."
              progressData={{
                topText:`${usedDiskPercentage}% of ${totalDiskSpace} GB`,
                percentage:usedRamPercentage,
                bottomText1:`${usedDiskSpace} GB in use`,
                bottomText2: `${freeDiskSpace} GB free`,
              }}
              deviceLoading={deviceLoading}
            />
          </Col>
          <Col xs="12" xl="3" className="mb-4">
            <ProgressCard
              cardTitle="RAM Utilization"
              tooltipId="ru"
              tooltipTitle="What does this mean?"
              tooltipContent="This indicates how much memory is being used by the device during operation. RAM health is considered good if there is consistently at least 1 GB of free memory available to ensure smooth performance and multitasking."
              progressData={{
                topText:`${usedRamPercentage}% of ${totalRam} GB`,
                percentage:usedRamPercentage,
                bottomText1:`${usedRam} GB in use`,
                bottomText2: `${freeRam} GB free`,
              }}
              deviceLoading={deviceLoading}
            />
          </Col>
          <Col xs="12" xl="3" className="mb-4">
            <ProgressCard
              cardTitle="CPU Utilization"
              tooltipId="cpu"
              tooltipTitle="What does this mean?"
              tooltipContent="This shows how much processing power the device is using during operation. CPU health is considered good if utilization stays below 80% during typical use, ensuring the device can handle tasks efficiently without slowing down."
              progressData={{
                topText:`${usedCpu}% in use`,
                percentage:usedCpu,
                bottomText1: `Good: 0–40%`,
                bottomText2: `Normal: 41–80%`,
                bottomText3: `High: 81–100%`,
              }}
              deviceLoading={deviceLoading}
            />
          </Col>
        </Row>
      </DevicePerformanceBlock> )}

      { displayGoogleStats && (<DevicePerformanceBlock
        title="Google Device Details"
        subtitle={`Essential hardware specs of this Google-managed device.`}
      >
        <div className="device-info">
          <Row>
            <Col xs="12" sm="6" md="3" className="mb-4">
              <InfoCard
                name="Operating System Version:"
                value={device?.os_version}
              />
            </Col>
            <Col xs="12" sm="6" md="3" className="mb-4">
              <InfoCard
                name="Platform Version:"
                value={device?.platform_version}
              />
            </Col>
            <Col xs="12" sm="6" md="3" className="mb-4">
              <InfoCard
                name="Recent Users:"
                list={device?.recent_users}
              />
            </Col>
            <Col xs="12" sm="6" md="3" className="mb-4">
              <InfoCard
                name="Last Sync:"
                value={device?.last_device_sync}
              />
            </Col>
          </Row>
        </div>
      </DevicePerformanceBlock>)}

      <DevicePerformanceBlock
        title="Device Details"
        subtitle={`Key hardware specs that may affect User experience.`}
      >
        <div className="device-info">
          <Row>
            <Col xs="12" sm="6" md="3" className="mb-4">
              <InfoCard
                name="Operating System:"
                value={systemName(device?.operating_system) || 'No data'}
              />
            </Col>
            <Col xs="12" sm="6" md="3" className="mb-4">
              <InfoCard
                name="CPU:"
                value={device?.cpu_info || device?.cpu_model || 'No data'}
              />
            </Col>
            <Col xs="12" sm="6" md="3" className="mb-4">
              <InfoCard
                name="Chrome Version:"
                value={device?.chrome_version || 'No data'}
              />
            </Col>
          </Row>
        </div>
      </DevicePerformanceBlock>

      {hddInfoResult(device?.hdd_info) !== 'hide' && (
        <DevicePerformanceBlock
          title="Hard Drive Capacity"
          subtitle="A snapshot of all the HDD volumes and their capacity."
        >
          <div className="device-info">
            {hddInfoResult(device?.hdd_info)}
          </div>
        </DevicePerformanceBlock>
      )}

      <DevicePerformanceBlock
        title="Browser"
        subtitle="Browser factors that may affect performance."
      >
        <div className="device-info">
          <Row>
            <Col xs="12" sm="6" md="3" className="mb-4">
              <InfoCard
                name="Avg # of Windows Open"
                value={device?.avg_active_window || 'Not Available'}
              />
            </Col>
            <Col xs="12" sm="6" md="3" className="mb-4">
              <InfoCard
                name="Avg # of Tabs Open"
                value={device?.avg_open_tab || 'Not Available'}
              />
            </Col>
          </Row>
        </div>
      </DevicePerformanceBlock>

      { displayGoogleStats && (<DevicePerformanceBlock
        title="Network Details"
        subtitle={`Details about local network connection and external IP address used.`}
      >
        <div className="device-info">
          <Row>
            <Col xs="12" sm="6" md="3" className="mb-4">
              <InfoCard
                name="IP Address:"
                value={device?.last_known_ip_address}
              />
            </Col>
            <Col xs="12" sm="6" md="3" className="mb-4">
              <InfoCard
                name="WAN address:"
                value={device?.last_known_wan_address}
              />
            </Col>
          </Row>
        </div>
      </DevicePerformanceBlock>)}

      <div className="tab-block">
        <Row className="align-items-center">
          <Col xl={12}>
            <div className="table-responsive">
              <UserDeviceTable
                fetch={fetchData}
                data={deviceActivities?.device_activities}
                isLoading={loading}
                onSortChange={onTableSortChange}
                disableSortBy
              />
            </div>
            {(deviceActivities?.device_activities?.length > 0 && paginationData) && <Pagination paginationData={paginationData} navigateTo={navigateToPage} />}
          </Col>
        </Row>
      </div>

      {isConfirmModalOpen && (
        <ActionModal
          isOpen={isConfirmModalOpen}
          confirm={onConfirmButtonClick}
          confirmLabel={toSentenceCase(action)}
          confirmDisabled={modalHasErrors}
          reject={onRejectButtonClick}
          rejectLabel="Cancel"
          headerText={`Are you sure you want to ${action}?`}
        >
          {ActionModalBody({ action, setModalHasError, isCustomReboot, setIsCustomReboot, rebootDelay: rebootDelayMinutes, setRebootDelay })}
        </ActionModal>
      )}
    </>
  );
};

const mapStateToProps = (state) => {
  const { error, loading, data, device } = state.UserStatus;
  const { deviceActivities, loading: deviceLoading } = state.Device;
  const { data: testDetailsData } = state.TestDetails;
  const { data: userProfile } = state.Profile;
  const { ai_features_enabled: isAiFeaturesEnabled } = state.Company.companyDetails;

  return {
    error,
    loading,
    data,
    device,
    deviceActivities,
    deviceLoading,
    testDetailsData,
    userProfile,
    isAiFeaturesEnabled,
  };
};

export default connect(mapStateToProps, {getUserDeviceActivities})(DevicePerformance);
